/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader, LogoCertX } from "@certx/shared";
import { accessAPI, downloadFile } from "@certx/shared";
import { Card } from "primereact/card";

const API_URL = process.env.REACT_APP_API_URL;

function getTipoArchivo(nombre) {
  const ext = nombre.split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)) return "imagen";
  if (ext === "txt") return "txt";
  return "otro";
}

function getPreviewUrl(archivoid) {
  return `${API_URL}/fabricanteimportador/proceso/preview/${archivoid}`;
}

export default function Proceso() {
  const { numeroProceso } = useParams();
  const [proceso, setProceso] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [error, setError] = useState(false);
  const [inactivo, setInactivo] = useState(false);
  const [descargando, setDescargando] = useState(null);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);

  // Cuando carga, va a buscar el proceso a la API
  useEffect(() => {
    if (numeroProceso) {
      accessAPI(
        "GET",
        `fabricanteimportador/proceso/${numeroProceso}`,
        null,
        (respuesta) => {
          // Si es REDIRECCION, redirige directamente
          if (respuesta.tipoUrl === "REDIRECCION" && respuesta.url) {
            window.location.href = respuesta.url;
            return;
          }

          // Si es HOSTING o no tiene tipoUrl pero tiene url, guarda el proceso
          setProceso(respuesta);

          // Si es HOSTING, busca los archivos
          if (respuesta.tipoUrl === "HOSTING") {
            accessAPI(
              "GET",
              `fabricanteimportador/proceso/archivos/${numeroProceso}`,
              null,
              (archivosRespuesta) => {
                setArchivos(archivosRespuesta);
              },
              () => {
                setArchivos([]);
              }
            );
          } else if (!respuesta.tipoUrl && respuesta.url) {
            // Retrocompatibilidad: procesos viejos sin tipoUrl redirigen
            window.location.href = respuesta.url;
          }
        },
        (respuesta) => {
          if (respuesta?.inactivo) {
            setInactivo(true);
          } else {
            setError(true);
          }
        }
      );
    }
  }, [numeroProceso]);

  // Descarga un archivo
  const descargarArchivo = (archivoid, nombreArchivo) => {
    setDescargando(archivoid);
    downloadFile(
      `fabricanteimportador/proceso/descargar/${archivoid}`,
      nombreArchivo,
      () => {
        setDescargando(null);
      },
      () => {
        setDescargando(null);
      }
    );
  };

  // Si el proceso está inactivo
  if (inactivo) {
    return (
      <div css={{ minHeight: "100vh", backgroundColor: "var(--beigeCertx)" }}>
        <div
          css={{
            width: "100%",
            height: "70px",
            backgroundColor: "var(--azulCertx)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 1px 4px 0px rgba(13,13,19,0.1)",
          }}
        >
          <LogoCertX altura="28px" blanco />
        </div>
        <div css={{ maxWidth: "500px", margin: "60px auto", padding: "0 20px", textAlign: "center" }}>
          <i
            className="pi pi-info-circle"
            css={{ fontSize: "3rem", color: "var(--azulCertx)", marginBottom: "20px" }}
          />
          <p css={{ color: "#333", fontSize: "1.1rem", lineHeight: "1.6" }}>
            Este proceso se encuentra inactivo.
          </p>
          <p css={{ color: "#666", fontSize: "0.95rem" }}>
            Comuníquese con el administrador.
          </p>
        </div>
      </div>
    );
  }

  // Si hubo un error
  if (error) {
    return (
      <div css={{ minHeight: "100vh", backgroundColor: "var(--beigeCertx)" }}>
        <div
          css={{
            width: "100%",
            height: "70px",
            backgroundColor: "var(--azulCertx)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 1px 4px 0px rgba(13,13,19,0.1)",
          }}
        >
          <LogoCertX altura="28px" blanco />
        </div>
        <div css={{ marginTop: "50px", textAlign: "center" }}>
          <p css={{ color: "#666", fontSize: "1.1rem" }}>
            No se encontró el proceso solicitado.
          </p>
        </div>
      </div>
    );
  }

  // Si es modo HOSTING y ya tenemos los datos
  if (proceso && proceso.tipoUrl === "HOSTING") {
    const seleccionado = archivoSeleccionado || (archivos.length > 0 ? archivos[0] : null);
    const tipoSeleccionado = seleccionado ? getTipoArchivo(seleccionado.archivo.nombredescarga) : null;
    const previewUrl = seleccionado ? getPreviewUrl(seleccionado.archivo.id) : null;

    return (
      <div css={{ minHeight: "100vh", backgroundColor: "var(--beigeCertx)" }}>
        <div
          css={{
            width: "100%",
            height: "70px",
            backgroundColor: "var(--azulCertx)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 1px 4px 0px rgba(13,13,19,0.1)",
          }}
        >
          <LogoCertX altura="28px" blanco />
        </div>
        <div css={{ maxWidth: "1100px", margin: "30px auto", padding: "0 20px" }}>
          <h1
            css={{
              textAlign: "center",
              fontSize: "1.5rem",
              color: "#333",
              marginBottom: "5px"
            }}
          >
            {proceso.nombrefantasia}
          </h1>
          {proceso.fabricanteimportador && (
            <p
              css={{
                textAlign: "center",
                fontSize: "1rem",
                color: "#666",
                marginBottom: "30px"
              }}
            >
              {proceso.fabricanteimportador.razonsocial}
            </p>
          )}

          {archivos.length === 0 ? (
            <Card>
              <p css={{ textAlign: "center", color: "var(--grisTextos)", padding: "20px 0", margin: 0 }}>
                No hay documentos disponibles para este proceso.
              </p>
            </Card>
          ) : (
            <div
              css={{
                display: "flex",
                gap: "16px",
                alignItems: "flex-start",
                "@media (max-width: 768px)": {
                  flexDirection: "column",
                },
              }}
            >
              {/* Lista de archivos - izquierda */}
              <Card
                css={{
                  width: "300px",
                  flexShrink: 0,
                  "@media (max-width: 768px)": {
                    width: "100%",
                  },
                }}
              >
                <h2
                  css={{
                    fontSize: "1rem",
                    color: "var(--azulCertx)",
                    marginTop: "0",
                    marginBottom: "12px",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #eee",
                    fontWeight: "400",
                  }}
                >
                  Documentos
                </h2>
                <div css={{ display: "flex", flexDirection: "column" }}>
                  {archivos.map((doc) => {
                    const tipo = getTipoArchivo(doc.archivo.nombredescarga);
                    const esSeleccionado = seleccionado?.id === doc.id;

                    return (
                      <div
                        key={doc.id}
                        onClick={() => setArchivoSeleccionado(doc)}
                        css={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "10px 8px",
                          cursor: "pointer",
                          borderRadius: "4px",
                          backgroundColor: esSeleccionado ? "rgba(93, 131, 195, 0.1)" : "transparent",
                          borderLeft: esSeleccionado ? "3px solid var(--azulCertx)" : "3px solid transparent",
                          "&:hover": {
                            backgroundColor: esSeleccionado ? "rgba(93, 131, 195, 0.1)" : "rgba(0, 0, 0, 0.03)",
                          },
                        }}
                      >
                        <div css={{ flex: 1, minWidth: 0 }}>
                          <div
                            css={{
                              fontSize: "0.85rem",
                              color: esSeleccionado ? "var(--azulCertx)" : "#333",
                              fontWeight: esSeleccionado ? "600" : "400",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <i
                              className={`pi ${tipo === "pdf" ? "pi-file-pdf" : tipo === "imagen" ? "pi-image" : tipo === "txt" ? "pi-file-edit" : "pi-file"}`}
                              css={{ marginRight: "6px", fontSize: "0.9em" }}
                            />
                            {doc.archivo.nombredescarga}
                          </div>
                          {doc.motivo && (
                            <div css={{ fontSize: "0.75rem", color: "#999", marginTop: "2px", paddingLeft: "20px" }}>
                              {doc.motivo}
                            </div>
                          )}
                        </div>
                        {descargando === doc.archivo.id ? (
                          <i className="pi pi-spin pi-spinner" css={{ color: "var(--azulCertx)", fontSize: "0.9em", padding: "2px", flexShrink: 0 }} />
                        ) : (
                          <i
                            className="pi pi-download"
                            onClick={(e) => {
                              e.stopPropagation();
                              descargarArchivo(doc.archivo.id, doc.archivo.nombredescarga);
                            }}
                            css={{
                              color: "var(--azulCertx)",
                              cursor: "pointer",
                              fontSize: "0.9em",
                              padding: "2px",
                              flexShrink: 0,
                              "&:hover": { opacity: 0.7 },
                            }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Visor - derecha */}
              <Card css={{ flex: 1, minWidth: 0 }}>
                {seleccionado && (
                  <>
                    <div
                      css={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                        paddingBottom: "8px",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <span css={{ fontSize: "0.95rem", color: "#333", fontWeight: "500" }}>
                        {seleccionado.archivo.nombredescarga}
                      </span>
                      {descargando === seleccionado.archivo.id ? (
                        <i className="pi pi-spin pi-spinner" css={{ color: "var(--azulCertx)", fontSize: "1.1em" }} />
                      ) : (
                        <i
                          className="pi pi-download"
                          onClick={() => descargarArchivo(seleccionado.archivo.id, seleccionado.archivo.nombredescarga)}
                          css={{
                            color: "var(--azulCertx)",
                            cursor: "pointer",
                            fontSize: "1.1em",
                            "&:hover": { opacity: 0.7 },
                          }}
                        />
                      )}
                    </div>

                    {tipoSeleccionado === "imagen" && (
                      <div css={{ textAlign: "center" }}>
                        <img
                          src={previewUrl}
                          alt={seleccionado.archivo.nombredescarga}
                          css={{
                            maxWidth: "100%",
                            maxHeight: "600px",
                            objectFit: "contain",
                            borderRadius: "4px",
                          }}
                        />
                      </div>
                    )}

                    {tipoSeleccionado === "pdf" && (
                      <iframe
                        src={`${previewUrl}#navpanes=0`}
                        title={seleccionado.archivo.nombredescarga}
                        css={{
                          width: "100%",
                          height: "calc(100vh - 250px)",
                          minHeight: "500px",
                          border: "1px solid #eee",
                          borderRadius: "4px",
                        }}
                      />
                    )}

                    {tipoSeleccionado === "txt" && (
                      <iframe
                        src={previewUrl}
                        title={seleccionado.archivo.nombredescarga}
                        css={{
                          width: "100%",
                          height: "calc(100vh - 250px)",
                          minHeight: "500px",
                          border: "1px solid #eee",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                        }}
                      />
                    )}

                    {tipoSeleccionado === "otro" && (
                      <div css={{ textAlign: "center", padding: "60px 20px" }}>
                        <i
                          className="pi pi-file"
                          css={{ fontSize: "3rem", color: "#ccc", marginBottom: "16px", display: "block" }}
                        />
                        <p css={{ color: "#666", margin: "0 0 12px 0" }}>
                          Vista previa no disponible para este tipo de archivo.
                        </p>
                        <span
                          onClick={() => descargarArchivo(seleccionado.archivo.id, seleccionado.archivo.nombredescarga)}
                          css={{
                            color: "var(--azulCertx)",
                            cursor: "pointer",
                            fontSize: "0.95rem",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          <i className="pi pi-download" css={{ marginRight: "6px" }} />
                          Descargar archivo
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Estado de carga (mientras busca datos o redirige)
  return (
    <div css={{ minHeight: "100vh", backgroundColor: "var(--beigeCertx)" }}>
      <div css={{ width: "100%", paddingTop: "20px", textAlign: "center" }}>
        <LogoCertX altura="28px" />
      </div>
      <div css={{ marginTop: "50px" }}>
        <Loader>Estamos redirigiendo a tus documentos.</Loader>
      </div>
    </div>
  );
}
